import { BarcodeDetectorScannerAdapter } from '@/shared/camera/barcode-detector-scanner-adapter';
import { JsQrScannerAdapter } from '@/shared/camera/jsqr-scanner-adapter';
import type { ScannerAdapter } from '@/shared/camera/scanner-adapter';
import {
  createValueScanCandidate,
  VALUE_SCAN_CANDIDATE_KINDS,
  type ValueScanCandidate,
  type ValueScanPurpose,
} from '@/shared/camera/value-scanner';

export async function scanBarcodeFrame(input: {
  adapter: ScannerAdapter;
  canvas: HTMLCanvasElement | null;
  onCandidates: (candidates: ValueScanCandidate[]) => Promise<unknown>;
  purpose: ValueScanPurpose;
  video: HTMLVideoElement | null;
}) {
  const { adapter, canvas, onCandidates, purpose, video } = input;

  if (!canvas) {
    return;
  }

  drawCurrentFrame({ canvas, video });
  const results = await adapter.scan(canvas);
  const candidates = results.flatMap(result => {
    const candidate = createValueScanCandidate({
      format: result.format,
      kind: VALUE_SCAN_CANDIDATE_KINDS.barcode,
      purpose,
      rawValue: result.rawValue,
    });

    return candidate ? [candidate] : [];
  });

  await onCandidates(candidates);
}

export class CompositeScannerAdapter implements ScannerAdapter {
  readonly id = 'composite';

  private readonly nativeAdapter = new BarcodeDetectorScannerAdapter();
  private readonly qrFallbackAdapter = new JsQrScannerAdapter();

  async isSupported() {
    return (await this.nativeAdapter.isSupported()) || this.qrFallbackAdapter.isSupported();
  }

  async scan(source: ImageBitmapSource) {
    if (await this.nativeAdapter.isSupported()) {
      const nativeResults = await this.nativeAdapter.scan(source);

      if (nativeResults.length > 0) {
        return nativeResults;
      }
    }

    return this.qrFallbackAdapter.scan(source);
  }
}

export async function waitForVideoElement(readVideo: () => HTMLVideoElement | null) {
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const video = readVideo();

    if (video) {
      return video;
    }

    await new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()));
  }

  throw new Error('Camera preview is not ready yet.');
}

export async function attachStreamToVideoElement(video: HTMLVideoElement, stream: MediaStream) {
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  await video.play();
}

function drawCurrentFrame(input: { canvas: HTMLCanvasElement; video: HTMLVideoElement | null }) {
  const { canvas, video } = input;

  if (!isVideoFrameReady(video)) {
    throw new Error('Camera frame is not ready yet.');
  }

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to read the camera frame.');
  }

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
}

function isVideoFrameReady(video: HTMLVideoElement | null): video is HTMLVideoElement {
  return Boolean(
    video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0,
  );
}
